import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import ContactSpreadsheetInvalidRow from './ContactSpreadsheetInvalidRow';
import { isAddressValid } from '../../../../../../util/account';
import useVirtualPageView from '../../../../../../apps/pro/services/analytics/useVirtualPageView';

const propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  initialRows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address_line_1: PropTypes.string,
      address_line_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      post_code: PropTypes.string,
      country: PropTypes.string,
      phone_number: PropTypes.string,
      email_address: PropTypes.string,
      date_of_birth: PropTypes.any,
    }).isRequired,
  ).isRequired,
  importData: PropTypes.array,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

const ContactSpreadsheetImportInvalidRowEditor = ({
  className,
  loading,
  initialRows,
  importData,
  onCancel,
  onContinue,
}) => {
  useVirtualPageView({
    title: 'Import Contacts - Fix Invalid Rows',
    path: '/contacts-import/invalids',
  });

  const classes = classNames({
    [className]: !!className,
  });

  const [rows, setRows] = useState(initialRows);
  const importedNumber = importData.length - rows.length;

  const isFormValid = rows.every(row => isAddressValid(row));

  return (
    <div className={classes}>
      <Alert type="info">
        <h4 className="alert-title">Errors found during import</h4>
        <p className="mb-0">
          <strong>
            {rows.length > 1 ? (
              <>A total of {rows.length} rows were</>
            ) : (
              <>The row bellow was</>
            )}{' '}
            found with errors
          </strong>
          . Please correct these errors and press Continue.
        </p>

        <p>
          {importedNumber === 0 && 'No contacts have been imported.'}
          {importedNumber === 1 && 'One contact has been imported.'}
          {importedNumber > 0 &&
            `A total of ${importedNumber} contacts have been imported.`}
        </p>
      </Alert>
      <div className="table-responsive">
        <table className="table table-striped text-sm">
          <thead>
            <tr>
              <th>&nbsp;</th>
              <th className="text-left">Address</th>
              <th>Mobile Phone</th>
              <th>Date of Birth</th>
              <th>&nbsp;</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => {
              return (
                <ContactSpreadsheetInvalidRow
                  key={index}
                  id={`row-${index}`}
                  row={row}
                  onChange={data =>
                    setRows(prev =>
                      prev.map((prevRow, prevRowIndex) => {
                        if (prevRowIndex === index) {
                          return {
                            ...prevRow,
                            ...data,
                          };
                        }

                        return prevRow;
                      }),
                    )
                  }
                  onDelete={() => {
                    setRows(prev =>
                      prev.filter((_, prevRowIndex) => {
                        return index !== prevRowIndex;
                      }),
                    );
                  }}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <Button
          btnStyle="link"
          onClick={onCancel}
          disabled={loading}
          className="ml-2"
          btnSize="sm"
        >
          Ignore All &amp; Return
        </Button>
        <Button
          onClick={() => {
            onContinue(rows);
          }}
          disabled={loading || !isFormValid}
          btnSize="sm"
          className="ml-2"
        >
          {loading ? 'Processing...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};

ContactSpreadsheetImportInvalidRowEditor.propTypes = propTypes;

export default ContactSpreadsheetImportInvalidRowEditor;
